import { useEffect, useRef, useState } from 'react'
import { Chart, ChartConfiguration, registerables } from 'chart.js'
import { PowerData } from '../../types/charts'
import 'chartjs-adapter-luxon'
import { Series, SeriesGroup, SeriesOptions } from '../../utils/series'
import { viewportShift } from '../../utils/history'
import { maxOf, minOf } from '../../utils/math'
import zoomPlugin from 'chartjs-plugin-zoom'
import { Box, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { sprintf } from 'sprintf-js'
import { midpointLinePlugin } from './midpointLinePlugin'
import { ChartSummary } from './ChartSummary'
import { lang } from '../../utils/lang'
import { isDebug } from '../../utils/misc'
import { ScatterPlot } from '@mui/icons-material'
import { bucketEnergyKwh, formatEnergyTotal, hasEnergyTotal } from '../../utils/energy'
import { quarterHourBandsPlugin, quarterHourLabels } from './quarterHourBands'

// Trigger onLoadMore* when the visible window's edge is within this many
// indices of the data's leftmost / rightmost real point.
const LOAD_MORE_THRESHOLD = 5

Chart.register(zoomPlugin, ...registerables)

export interface PowerHistoryChartProps {
  id: string
  spinner?: boolean
  data: PowerData[]
  labels?: string[]
  options: SeriesOptions
  noLabels?: boolean
  vat?: number
  onClick?: () => void
  onLoadMoreLeft?: () => void
  onLoadMoreRight?: () => void
  onViewportChange?: (fromMs: number, toMs: number) => void
}

export function PowerHistoryChart(props: PowerHistoryChartProps): React.ReactElement {
  const { id, data, noLabels, options, spinner, onLoadMoreLeft, onLoadMoreRight, onViewportChange } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const [main, setMain] = useState('')
  const [total, setTotal] = useState('')
  const [left, setLeft] = useState('')
  const [right, setRight] = useState('')
  const color = options.variant === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.main

  // Start time (epoch ms) of the leftmost group of the previous render, used
  // to work out how far the index-based viewport must move when data grows.
  const prevFirstGroupFromRef = useRef<number | null>(null)
  // Lives across useEffect re-runs — Chart.getChart(id) would return nothing
  // because the previous effect's cleanup destroys the chart before the next
  // effect runs, so we can't read the prior viewport from the chart itself.
  const viewportRef = useRef<{ min: number, max: number } | null>(null)
  // Track previous steps so we know when to invalidate the saved viewport
  // (index-space changes meaning when scale changes).
  const prevStepsRef = useRef<string | undefined>(undefined)

  // Update values for summary.
  const setGroup = (summaryValues: number | null | undefined, group: SeriesGroup | undefined): void => {
    if (!group) {
      setMain('—')
      setTotal('—')
      setLeft('')
      setRight('')
    } else {
      const min = group.members.length > 0 ? group.members[0].min_power : null
      const max = group.members.length > 0 ? group.members[0].max_power : null
      if (summaryValues === null || summaryValues === undefined || min === null || max === null) {
        setMain('—')
        setLeft('—,— kW - —,— kW')
      } else {
        const digits = isDebug('high-accuracy') ? '%.3f' : '%.1f'
        setMain(`${sprintf(digits, summaryValues / 1000).replace('.', ',')}`)
        setLeft(`${sprintf(`${digits} kW - ${digits} kW`, min / 1000, max / 1000).replace(/\./g, ',')}`)
      }
      setRight(lang.timeLabel(group.from, options.steps || 'hour'))
      // Energy of the bucket: average power × hours of the bucket, the newest
      // (current) bucket only up to now. Shown for day, week, month and year.
      if (hasEnergyTotal(options.steps)) {
        setTotal(formatEnergyTotal(bucketEnergyKwh(summaryValues, group, Date.now()), isDebug('high-accuracy')))
      }
    }
  }

  useEffect(() => {
    // When scale changes, the prior viewport's indices belong to a different
    // bucket count and have no meaning — reset so we fall back to the new
    // initial from..to window.
    const stepsChanged = prevStepsRef.current !== undefined && prevStepsRef.current !== options.steps
    prevStepsRef.current = options.steps
    if (stepsChanged) {
      viewportRef.current = null
    }

    const { labels, result, groups, startIndex, endIndex } = Series.simplify(data as PowerData[], { ...options, key: 'power' })

    const prevFirstGroupFrom = prevFirstGroupFromRef.current
    prevFirstGroupFromRef.current = groups.length > 0 ? groups[0].from : null

    // Helper to find the current midpoint and refresh the summary values.
    const refreshSummary = (chart: Chart): void => {
      const { min, max } = chart.scales.x
      const idx = Math.floor((min + max) / 2)
      setGroup(result[idx], groups[idx])
    }

    const maybeLoadMore = (chart: Chart): void => {
      if (data.length <= 2) return
      const min = chart.scales.x.min as number
      const max = chart.scales.x.max as number
      if (onLoadMoreLeft && min < LOAD_MORE_THRESHOLD) {
        onLoadMoreLeft()
      }
      if (onLoadMoreRight && groups.length > 0 && max > groups.length - 1 - LOAD_MORE_THRESHOLD) {
        onLoadMoreRight()
      }
    }

    // Translate the chart's index-based viewport back to a wall-clock time
    // range so the parent can use it as the anchor for scale switches.
    const reportViewport = (chart: Chart): void => {
      if (!onViewportChange || groups.length === 0) return
      const min = chart.scales.x.min as number
      const max = chart.scales.x.max as number
      const lastIdx = groups.length - 1
      const minIdx = Math.max(0, Math.min(lastIdx, Math.floor(min)))
      const maxIdx = Math.max(0, Math.min(lastIdx, Math.floor(max)))
      onViewportChange(groups[minIdx].from, groups[maxIdx].to)
    }

    const yMax = Math.ceil(maxOf(result))
    const yMin = Math.floor(minOf(result))

    const usedLabels: string[] = labels as string[]
    const usedData: (number | null)[] = result

    if (options.panX) {
      const add = Math.floor((endIndex - startIndex) / 2)
      for (let i = 0; i < add; i++) {
        usedLabels.push('')
        usedData.push(null)
      }
    }

    // 15-min view: replace cluttered :00/:15/:30/:45 labels with bare hour
    // numbers and add alternating background bands to keep hours readable.
    const finalLabels = options.steps === 'quarter'
      ? quarterHourLabels(usedLabels, groups)
      : usedLabels

    const config: ChartConfiguration = {
      type: 'line',

      data: {
        labels: finalLabels,
        datasets: [
          {
            type: options.steps === 'minute' ? 'line' : options.type,
            data: usedData,
            label: t('Consumption ( kW )'),
            showLine: true,
            cubicInterpolationMode: 'monotone',
            pointRadius: 0,
            borderWidth: 2,
            borderRadius: 4,
            backgroundColor: color,
            borderColor: color,
            tension: 0.3,
            animations: { tension: { easing: 'linear' } }
          },
        ]
      },

      plugins: [
        midpointLinePlugin(theme.palette.chartMidpointLine.main),
        {
          id: 'refreshSummary',
          afterRender: (chart) => refreshSummary(chart)
        },
        ...(options.steps === 'quarter' ? [quarterHourBandsPlugin(groups)] : []),
      ],

      options: {
        animation: false,
        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
            position: 'bottom'
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
              onPanComplete: ({ chart }) => {
                refreshSummary(chart)
                viewportRef.current = {
                  min: chart.scales.x.min as number,
                  max: chart.scales.x.max as number,
                }
                reportViewport(chart)
                maybeLoadMore(chart)
              }
            },
          }
        },

        scales: {
          x: {
            min: ['minute', 'year'].includes(options.steps || '') ? startIndex : startIndex + 1, // Note: this value can affect the matching of the labels and the midline.
            max: ['month', 'year'].includes(options.steps || '') ? endIndex : endIndex + 1,
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              // For quarter scale only 1-in-4 labels are non-empty (hours at
              // :00). Default autoSkip would land its first tick anywhere
              // from index 0 to 3 depending on pan, so labels would blink
              // in and out as you scrolled. Drawing every tick keeps the
              // :00 labels stable; the rest are blank strings anyway.
              autoSkip: options.steps === 'quarter' ? false : undefined,
              font: {
                size: 10
              },
            },
            display: !noLabels
          },

          y: {
            min: (yMin < 0 || isDebug('high-accuracy')) ? undefined : 0,
            max: yMax,
            grid: {
              color: '#dfdfdf',
              lineWidth: 0.075
            },
            ticks: {
              color: theme.palette.grey[400],
              minRotation: 0,
              callback: (value) => sprintf(`%.${isDebug('high-accuracy') ? 3 : 1}f`, value as number / 1000) + 'kW'
            },
            title: {
              display: false,
              text: t('kW')
            }
          }
        },

        onClick: () => {
          props.onClick && props.onClick()
        },
      }
    }

    // Preserve the viewport across data growth, so that loading an older or
    // newer page keeps the user looking at the same moment in time. The shift
    // is derived from where the previously leftmost group ended up: prepended
    // pages move it right, appended pages do not move it at all. Without that
    // distinction, appending a page scrolled the view forward by the size of
    // the page. Without the preservation, an unrelated re-render (e.g.
    // /history/oldest resolving) would snap the chart back to from..to.
    const shift = viewportShift(prevFirstGroupFrom, groups)
    const saved = viewportRef.current
    if (saved !== null && shift >= 0 && config.options?.scales?.x) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const xScale = config.options.scales.x as any
      xScale.min = saved.min + shift
      xScale.max = saved.max + shift
      viewportRef.current = { min: xScale.min as number, max: xScale.max as number }
    }

    Chart.getChart(id)?.destroy()
    const currentChart = new Chart(
      document.getElementById(id) as HTMLCanvasElement,
      config as ChartConfiguration
    )

    // Tell the parent about the chart's initial viewport, so a scale switch
    // without any prior pan still has a meaningful anchor.
    reportViewport(currentChart)

    return () => {
      currentChart.destroy()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, data, options, t, color, theme.palette.grey, noLabels, onLoadMoreLeft, onLoadMoreRight, onViewportChange, props])

  return (
    <Box>
      <ChartSummary sx={{ mb: 2 }} main={main} total={total} steps={options.steps} unit="kW" left={left} right={right}/>
      <Box sx={{
        display: spinner ? 'relative' : 'none',
        height: 0,
        pl: 4
      }} >
        <Box className="rotating-slow" sx={{ display: 'inline-block' }}>
          <ScatterPlot sx={{ color, fontSize: 100 }}/>
        </Box>
      </Box>
      <canvas id={id} data-cy={`canvas-${id}`}/>
    </Box>
  )
}
